// Import an as-built KML or KMZ (Google Earth, Map Marker and similar field apps).
// Nothing is written until the plan has been reviewed: the file is checked first and the operator
// sees exactly which sites and cables would be created, and what could not be joined.

import { FileUp, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { ApiError, api, type ImportPlan } from '../api'
import { useApp } from '../context'
import { fmtM } from '../format'
import { PanelShell } from './PanelShell'
import { Button, Callout, Section, cx } from './ui'

const input = 'h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm'
const label = 'mb-1 block text-sm font-medium'

export function ImportPanel() {
  const { refreshNetwork, back, notify, open } = useApp()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<{ name: string; content: string } | null>(null)
  const [toleranceM, setToleranceM] = useState(15)
  const [status, setStatus] = useState<'active' | 'planned'>('active')
  const [locality, setLocality] = useState('')
  const [ratio, setRatio] = useState(8)
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [busy, setBusy] = useState<'plan' | 'apply' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const pick = async (f: File) => {
    setError(null)
    setPlan(null)
    const buf = new Uint8Array(await f.arrayBuffer())
    let binary = ''
    for (let i = 0; i < buf.length; i += 0x8000) binary += String.fromCharCode(...buf.subarray(i, i + 0x8000))
    setFile({ name: f.name, content: btoa(binary) })
  }

  const options = () => ({ toleranceM, status, splitterRatio: ratio, ...(locality.trim() ? { locality: locality.trim() } : {}) })

  const check = async () => {
    if (!file) return
    setBusy('plan')
    setError(null)
    try {
      setPlan(await api.importPlan(file.name, file.content, options()))
    } catch (e) {
      setError(e instanceof ApiError && e.issues?.length ? e.issues.join('; ') : e instanceof Error ? e.message : 'Could not read this file')
    } finally {
      setBusy(null)
    }
  }

  const apply = async () => {
    if (!file || !plan) return
    setBusy('apply')
    setError(null)
    try {
      const r = await api.importApply(file.name, file.content, options())
      refreshNetwork()
      notify(`Imported ${r.nodes.length} site(s) and ${r.cables.length} cable(s) from ${file.name}`)
      if (r.failures.length) setError(`${r.failures.length} item(s) could not be saved: ${r.failures.slice(0, 3).map((f) => `${f.source} (${f.message})`).join('; ')}`)
      else {
        setPlan(null)
        setFile(null)
        open({ kind: 'quality' }) // a good first stop after an import: what still needs splicing
      }
    } catch (e) {
      setError(e instanceof ApiError && e.issues?.length ? e.issues.join('; ') : e instanceof Error ? e.message : 'Import failed')
    } finally {
      setBusy(null)
    }
  }

  const s = plan?.summary
  return (
    <PanelShell title="Import KML / KMZ" subtitle="As-built from Google Earth or a field app" onClose={back}>
      <Section title="File">
        <input
          ref={fileRef}
          type="file"
          id="import-file"
          accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
          className="sr-only"
          onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => fileRef.current?.click()}>
            <FileUp className="h-4 w-4" aria-hidden /> Choose file
          </Button>
          <span className="num text-sm text-muted">{file ? file.name : 'No file chosen'}</span>
        </div>
        <p className="mt-2 text-xs text-muted">
          Points become sites (NAP, LCP, closure, pole, handhole) and lines become cables. The kind is read from the folder or the name, and each cable end joins the
          nearest site within the snap distance. Splices are not in a KML, so imported cables arrive with no cores in use.
        </p>
      </Section>

      <Section title="How to read the file">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="imp-tol">
              Snap distance
            </label>
            <div className="flex items-center gap-2">
              <input id="imp-tol" type="range" min={2} max={60} step={1} value={toleranceM} onChange={(e) => setToleranceM(Number(e.target.value))} className="w-full" />
              <span className="num w-12 text-sm">{toleranceM} m</span>
            </div>
            <p className="mt-1 text-xs text-muted">How close a cable end must be to a box to count as joined there.</p>
          </div>
          <div>
            <label className={label} htmlFor="imp-ratio">
              Splitter for imported NAPs and LCPs
            </label>
            <select id="imp-ratio" className={input} value={ratio} onChange={(e) => setRatio(Number(e.target.value))}>
              {[2, 4, 8, 16, 32, 64].map((r) => (
                <option key={r} value={r}>
                  1:{r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label} htmlFor="imp-locality">
              Barangay / municipality
            </label>
            <input id="imp-locality" className={input} value={locality} onChange={(e) => setLocality(e.target.value)} placeholder="e.g. Burgos, Rodriguez, Rizal" maxLength={120} />
          </div>
          <fieldset>
            <legend className={label}>Status</legend>
            <div className="flex gap-4 text-sm">
              {(['active', 'planned'] as const).map((v) => (
                <label key={v} className="flex items-center gap-1.5">
                  <input type="radio" name="imp-status" checked={status === v} onChange={() => setStatus(v)} /> {v === 'active' ? 'Installed' : 'Planned'}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="mt-3">
          <Button type="button" variant="primary" busy={busy === 'plan'} disabled={!file} onClick={check}>
            Check file
          </Button>
        </div>
        {error && (
          <Callout tone="danger" title="Not imported">
            {error}
          </Callout>
        )}
      </Section>

      {plan && s && (
        <>
          <Section title="What this would add">
            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <Tile n={s.sitesNew} what="new sites" />
              <Tile n={s.cablesNew} what="new cables" />
              <Tile n={s.sitesDuplicate} what="already recorded" muted />
              <Tile n={s.errors} what="cannot import" tone={s.errors ? 'danger' : undefined} />
            </div>
            <div className="mt-3">
              <Button type="button" variant="primary" busy={busy === 'apply'} disabled={!s.sitesNew && !s.cablesNew} onClick={apply}>
                <Upload className="h-4 w-4" aria-hidden /> Import {s.sitesNew} site(s) and {s.cablesNew} cable(s)
              </Button>
            </div>
          </Section>

          {plan.cables.length > 0 && (
            <Section title={`Cables (${plan.cables.length})`}>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="py-1">Name</th>
                    <th>Joins</th>
                    <th className="text-right">Length</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.cables.slice(0, 200).map((c) => (
                    <tr key={c.ref} className="border-t border-border">
                      <td className="py-1.5">
                        <span className="num">{c.name}</span>
                        <span className="ml-1 text-xs text-muted">
                          {c.fiberCount}F {c.tier}
                        </span>
                      </td>
                      <td className="text-xs">
                        {c.fromLabel} → {c.toLabel}
                      </td>
                      <td className="num text-right text-xs">{fmtM(c.lengthM)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {plan.sites.length > 0 && (
            <Section title={`Sites (${plan.sites.length})`}>
              <ul className="space-y-1 text-sm">
                {plan.sites.slice(0, 200).map((site) => (
                  <li key={site.ref} className="flex items-center justify-between border-t border-border py-1.5">
                    <span className="num">{site.name}</span>
                    <span className="text-xs text-muted">
                      {site.kind.toUpperCase()}
                      {site.duplicateOfNodeId ? ' · already recorded, will be skipped' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {plan.issues.length > 0 && (
            <Section title={`Needs attention (${plan.issues.length})`}>
              <ul className="space-y-2 text-sm">
                {plan.issues.slice(0, 100).map((i, n) => (
                  <li key={n} className={cx('border-l-2 pl-2', i.severity === 'error' ? 'border-danger' : 'border-warn')}>
                    <span className="num">{i.source}</span>
                    <p className="text-xs text-muted">{i.message}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </PanelShell>
  )
}

function Tile({ n, what, muted, tone }: { n: number; what: string; muted?: boolean; tone?: 'danger' }) {
  return (
    <div className="rounded-lg border border-border p-2">
      <p className={cx('num text-xl font-semibold', tone === 'danger' && n > 0 ? 'text-danger' : muted ? 'text-muted' : '')}>{n}</p>
      <p className="text-xs text-muted">{what}</p>
    </div>
  )
}
